import { useConstructor } from '@/shared/lib/customHooks';
import { useEffect, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NewStudyStep5Props } from './newStudyStep5.types';

/**
 * Drives the last step of the new-study wizard, exposing the finish action and the shortcut that creates a return shipping label for the new study. Marks the step data as loaded on construction, scrolls the window and the .app-body container back to the top on mount, and publishes the finish handler as handleNavigation through the imperative ref.
 *
 * @param defaultData - Wizard data holding the created study.
 * @param setIsLoadedData - Setter marking the step data as loaded.
 * @param handleClick - Parent wizard handler advancing the flow.
 * @param ref - Imperative ref exposing handleNavigation.
 * @returns The finish and create-return-shipping handlers.
 */
const useNewStudyStep5 = (
  { defaultData, setIsLoadedData, handleClick }: NewStudyStep5Props,
  ref: any,
) => {
  const navigate = useNavigate();

  /**
   * Completes the final step of the new-study wizard when its bottom action button is pressed. Calls the parent handleClick with an empty step payload and the forward flag set to true.
   *
   * @returns Nothing.
   */
  const onClickBottomButton = () => {
    handleClick({}, true);
  };

  /**
   * Sends the user from the finished new-study wizard to the return-shipping creation page of the study just created. Navigates to the create-return-shipping route of the study id in defaultData, replacing the current history entry.
   *
   * @returns Nothing.
   */
  const onClickCreateReturnShipping = () => {
    navigate(`/studies/${defaultData?.study?.id}/create-return-shipping`, {
      replace: true,
    });
  };

  useImperativeHandle(ref, () => ({
    handleNavigation: onClickBottomButton,
  }));

  useConstructor(() => {
    setIsLoadedData(true);
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const bodyElement = document.querySelector('.app-body');
    if (bodyElement) {
      bodyElement.scroll(0, 0);
    }
  }, []);

  return { onClickBottomButton, onClickCreateReturnShipping };
};

export { useNewStudyStep5 };
